import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
} from "react";
import api from "../services/api";

const MenuContext = createContext();

export const MenuProvider = ({ children }) => {
  const [menuData, setMenuData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const inFlight = useRef(false); // 防止重复请求

  const CACHE_KEY = "menu_cache";
  const CACHE_TTL = 1000 * 60 * 10;

  useEffect(() => {
    const nav = performance.getEntriesByType("navigation")[0];
    const isReload =
      (nav && nav.type === "reload") ||
      // 兼容极旧浏览器
      (window.performance &&
        window.performance.navigation &&
        window.performance.navigation.type === 1);

    if (isReload) {
      sessionStorage.removeItem(CACHE_KEY); // 刷新时清缓存
    }

    fetchMenu(); // 照常请求（若缓存被清，会重新拉）
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const readCache = () => {
    try {
      const cached = sessionStorage.getItem(CACHE_KEY);
      if (!cached) return null;
      const { at, data } = JSON.parse(cached);
      if (Date.now() - at > CACHE_TTL) return null;
      return data;
    } catch {
      return null;
    }
  };

  const writeCache = (data) => {
    try {
      sessionStorage.setItem(
        CACHE_KEY,
        JSON.stringify({ at: Date.now(), data })
      );
    } catch {}
  };


  const filterChildren = (list, typeName) => {
    const children = list?.filter(({ type }) => type === typeName)?.[0]
      ?.children;
    return children?.filter(({ slug }) => slug !== "all") || [];
  };
  const fetchMenu = async () => {
    if (menuData.length > 0 || inFlight.current) return;
    inFlight.current = true;
    setLoading(true);
    setError(null);

    try {
      const cached = readCache();
      if (cached) {
        setMenuData(cached);
        return;
      }

      const url = "api/v2/page-structure/";
      // const projectUrl = "/api/v2/pages/?type=project.Project&fields=*";
      const structureData = await api.get(url);
      const filterStructureData = Array.isArray(structureData)
        ? structureData.filter((item) => item.type !== "home.AboutInfo")
        : [];

      const menuConfig = [
        {
          slug: "about",
          title: "About",
          type: "project.DummyAboutHolder",
          children: [
            ...filterChildren(filterStructureData, "project.DummyAboutHolder"),
          ],
        },
        {
          slug: "projects",
          title: "Projects",
          type: "project.DummyProjectHolder",
          children: [
            {
              slug: "signature_projects",
              title: "Signature Projects",
              href: "/projects?pageType=signature",
            },
            {
              slug: "archive",
              title: "Archive",
              href: "/projects?pageType=archive",
            },
          ],
        },
        {
          slug: "education",
          title: "Education",
          type: "landingpage.EducationDummyHolder",
          children: [
            {
              slug: "all",
              title: "All",
              type: "landingpage.EducationPage",
            },

            ...filterChildren(
              filterStructureData,
              "landingpage.EducationDummyHolder"
            ),
          ],
        },
        {
          slug: "research",
          title: "Research",
          type: "landingpage.ResearchDummyHolder",
          children: [
            {
              slug: "all",
              title: "All",
              type: "landingpage.ResearchPage",
            },
            ...filterChildren(
              filterStructureData,
              "landingpage.ResearchDummyHolder"
            ),
          ],
        },
        {
          slug: "contact",
          title: "Contact",
          type: "home.AboutInfo",
          children: [
            {
              slug: "",
              title: "",
              type: "home.AboutInfo",
            },
          ],
        },
      ];

      setMenuData(menuConfig);
      writeCache(menuConfig);
    } catch (err) {
      setError(err?.message || "loading error");
    } finally {
      setLoading(false);
      inFlight.current = false;
    }
  };

  // 主动刷新（清缓存后强制请求）
  const refresh = async () => {
    sessionStorage.removeItem(CACHE_KEY);
    setMenuData([]);
    await fetchMenu();
  };

  useEffect(() => {
    fetchMenu();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <MenuContext.Provider
      value={{
        menuData,
        loading,
        error,
        refresh,
      }}
    >
      {children}
    </MenuContext.Provider>
  );
};

export const useMenu = () => {
  const ctx = useContext(MenuContext);
  if (!ctx) throw new Error("useMenu 必须在 MenuProvider 内使用");
  return ctx;
};
